(() => {
  'use strict';

  const q = (selector, scope = document) => scope.querySelector(selector);
  const qa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = () => window.matchMedia('(pointer: fine)').matches;

  const initMenus = (scope = document) => {
    qa('[data-site-header]', scope).forEach((header) => {
      if (header.dataset.menuBound === 'true') return;
      header.dataset.menuBound = 'true';
      const toggle = q('[data-menu-toggle]', header);
      const shell = q('[data-mobile-shell]', header);
      const drawer = q('[data-mobile-drawer]', header);
      if (!toggle || !shell || !drawer) return;
      let lastFocus = null;
      const close = () => {
        shell.hidden = true;
        document.body.classList.remove('menu-open');
        toggle.setAttribute('aria-expanded', 'false');
        lastFocus?.focus();
      };
      const open = () => {
        lastFocus = document.activeElement;
        shell.hidden = false;
        document.body.classList.add('menu-open');
        toggle.setAttribute('aria-expanded', 'true');
        requestAnimationFrame(() => q('a,button', drawer)?.focus());
      };
      toggle.addEventListener('click', () => shell.hidden ? open() : close());
      qa('[data-menu-close]', shell).forEach((button) => button.addEventListener('click', close));
      shell.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
        if (event.key !== 'Tab') return;
        const items = qa('a,button,input,[tabindex]:not([tabindex="-1"])', drawer).filter((item) => !item.disabled);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      });
      qa('a', drawer).forEach((link) => link.addEventListener('click', close));
    });
  };

  const initHeaderScroll = () => {
    const headers = qa('[data-site-header]');
    if (!headers.length) return;
    const update = () => headers.forEach((header) => header.classList.toggle('is-scrolled', window.scrollY > 24));
    window.addEventListener('scroll', update, { passive: true });
    update();
  };

  const initReveal = (scope = document) => {
    const items = qa('[data-reveal]:not([data-reveal-bound])', scope);
    if (!items.length) return;
    items.forEach((item) => item.dataset.revealBound = 'true');
    if (!document.body.classList.contains('has-reveal') || reducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    items.forEach((item) => observer.observe(item));
  };

  const wrapHeadingWords = (heading) => {
    if (!heading || heading.dataset.splitBound === 'true') return;
    heading.dataset.splitBound = 'true';
    let index = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const parts = child.textContent.split(/(\s+)/);
          const fragment = document.createDocumentFragment();
          parts.forEach((part) => {
            if (/^\s+$/.test(part)) { fragment.appendChild(document.createTextNode(part)); return; }
            if (!part) return;
            const outer = document.createElement('span');
            outer.className = 'split-word';
            const inner = document.createElement('i');
            inner.style.setProperty('--word-index', index++);
            inner.textContent = part;
            outer.appendChild(inner);
            fragment.appendChild(outer);
          });
          child.replaceWith(fragment);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child);
        }
      });
    };
    walk(heading);
  };

  const animateNumber = (element, from, to, duration = 1800) => {
    if (!element) return;
    if (reducedMotion()) { element.textContent = Math.round(to).toLocaleString(); return; }
    const start = performance.now();
    const tick = (now) => {
      const t = clamp((now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      element.textContent = Math.round(from + (to - from) * eased).toLocaleString();
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const initAlpineHeroes = (scope = document) => {
    qa('[data-alpine-hero]', scope).forEach((hero) => {
      if (hero.dataset.heroBound === 'true') return;
      hero.dataset.heroBound = 'true';
      const heading = q('[data-split-heading]', hero);
      const terrain = q('[data-hero-terrain]', hero);
      const spotlight = q('[data-hero-spotlight]', hero);
      const layers = qa('[data-hero-layer]', hero);
      const path = q('[data-hero-trail]', hero);
      const glow = q('[data-hero-trail-glow]', hero);
      const marker = q('[data-hero-marker]', hero);
      const altimeter = q('[data-altimeter-value]', hero);
      wrapHeadingWords(heading);
      requestAnimationFrame(() => hero.classList.add('is-ready'));

      if (path) {
        const length = path.getTotalLength();
        [path, glow].filter(Boolean).forEach((line) => {
          line.style.strokeDasharray = `${length}`;
          line.style.strokeDashoffset = `${length}`;
        });
        if (marker) {
          const point = path.getPointAtLength(0);
          marker.setAttribute('transform', `translate(${point.x} ${point.y})`);
        }
        const runTrail = () => {
          if (reducedMotion()) {
            [path, glow].filter(Boolean).forEach((line) => line.style.strokeDashoffset = '0');
            if (marker) {
              const point = path.getPointAtLength(length);
              marker.setAttribute('transform', `translate(${point.x} ${point.y})`);
            }
            animateNumber(altimeter, 0, 2850, 0);
            return;
          }
          const start = performance.now();
          const duration = 4200;
          const frame = (now) => {
            const t = clamp((now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 2.4);
            [path, glow].filter(Boolean).forEach((line) => line.style.strokeDashoffset = `${length * (1 - eased)}`);
            if (marker) {
              const point = path.getPointAtLength(length * eased);
              marker.setAttribute('transform', `translate(${point.x} ${point.y})`);
            }
            if (altimeter) altimeter.textContent = Math.round(2850 * eased).toLocaleString();
            if (t < 1) requestAnimationFrame(frame);
          };
          requestAnimationFrame(frame);
        };
        window.setTimeout(runTrail, 550);
      }

      if (!reducedMotion() && finePointer()) {
        hero.addEventListener('pointermove', (event) => {
          const rect = hero.getBoundingClientRect();
          const x = clamp((event.clientX - rect.left) / rect.width);
          const y = clamp((event.clientY - rect.top) / rect.height);
          spotlight?.style.setProperty('--spot-x', `${x * 100}%`);
          spotlight?.style.setProperty('--spot-y', `${y * 100}%`);
          if (terrain) {
            terrain.style.setProperty('--terrain-x', `${(x - .5) * 18}px`);
            terrain.style.setProperty('--terrain-y', `${(y - .5) * 14}px`);
            terrain.style.setProperty('--terrain-rx', `${(y - .5) * -8}deg`);
            terrain.style.setProperty('--terrain-ry', `${(x - .5) * 10}deg`);
          }
        });
        hero.addEventListener('pointerleave', () => {
          if (!terrain) return;
          ['--terrain-x','--terrain-y','--terrain-rx','--terrain-ry'].forEach((prop) => terrain.style.removeProperty(prop));
        });
      }

      if (!reducedMotion() && document.body.classList.contains('has-parallax')) {
        let ticking = false;
        const update = () => {
          const rect = hero.getBoundingClientRect();
          const progress = clamp(-rect.top / Math.max(rect.height, 1), 0, 1);
          layers.forEach((layer) => {
            const strength = Number(layer.dataset.heroLayer || .08);
            layer.style.transform = `translate3d(0,${progress * rect.height * strength}px,0)`;
          });
          ticking = false;
        };
        window.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        }, { passive: true });
        update();
      }
    });
  };

  const initRouteSelectors = (scope = document) => {
    qa('[data-route-selector]', scope).forEach((selector) => {
      if (selector.dataset.routeBound === 'true') return;
      selector.dataset.routeBound = 'true';
      const tabs = qa('[data-route-tab]', selector);
      const panels = qa('[data-route-panel]', selector);
      const activate = (index, focus = false) => {
        tabs.forEach((tab, i) => {
          const active = i === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        panels.forEach((panel, i) => {
          const active = i === index;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(index));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowDown','ArrowUp','ArrowRight','ArrowLeft','Home','End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          else next = (index - 1 + tabs.length) % tabs.length;
          activate(next, true);
        });
      });
    });
  };

  const initAlpineAscents = (scope = document) => {
    qa('[data-alpine-ascent]', scope).forEach((section) => {
      if (section.dataset.ascentBound === 'true') return;
      section.dataset.ascentBound = 'true';
      const stage = q('.alpine-ascent__stage', section);
      const chapters = qa('[data-ascent-chapter]', section);
      const dots = qa('[data-ascent-dot]', section);
      const path = q('[data-ascent-path]', section);
      const glow = q('[data-ascent-glow]', section);
      const marker = q('[data-ascent-marker]', section);
      const altitude = q('[data-ascent-altitude]', section);
      const bearing = q('[data-ascent-bearing]', section);
      if (!chapters.length) return;
      let current = 0;
      let length = 0;
      if (path) {
        length = path.getTotalLength();
        [path, glow].filter(Boolean).forEach((line) => {
          line.style.strokeDasharray = `${length}`;
          line.style.strokeDashoffset = `${length}`;
        });
      }
      const activate = (index) => {
        current = clamp(index, 0, chapters.length - 1);
        chapters.forEach((chapter, i) => chapter.classList.toggle('is-active', i === current));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === current));
        const chapter = chapters[current];
        if (altitude) altitude.textContent = Number(chapter.dataset.altitude || 0).toLocaleString();
        if (bearing) bearing.textContent = chapter.dataset.bearing || '';
      };
      dots.forEach((dot, index) => dot.addEventListener('click', () => {
        activate(index);
        if (window.innerWidth > 900 && stage) {
          const travel = Math.max(stage.offsetHeight - window.innerHeight, 1);
          window.scrollTo({ top: stage.offsetTop + travel * (index / Math.max(chapters.length - 1, 1)), behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
      }));
      const update = () => {
        if (!stage || window.innerWidth <= 900 || reducedMotion()) {
          if (path) path.style.strokeDashoffset = '0';
          if (glow) glow.style.strokeDashoffset = '0';
          if (marker && path) {
            const point = path.getPointAtLength(length * ((current + 1) / chapters.length));
            marker.setAttribute('transform', `translate(${point.x} ${point.y})`);
          }
          return;
        }
        const rect = stage.getBoundingClientRect();
        const travel = Math.max(stage.offsetHeight - window.innerHeight, 1);
        const progress = clamp(-rect.top / travel);
        const index = Math.min(chapters.length - 1, Math.floor(progress * chapters.length));
        if (index !== current) activate(index);
        if (path) path.style.strokeDashoffset = `${length * (1 - progress)}`;
        if (glow) glow.style.strokeDashoffset = `${length * (1 - progress)}`;
        if (marker && path) {
          const point = path.getPointAtLength(length * progress);
          marker.setAttribute('transform', `translate(${point.x} ${point.y})`);
        }
        const activeChapter = chapters[index];
        const nextChapter = chapters[Math.min(index + 1, chapters.length - 1)];
        const segment = progress * chapters.length - index;
        const from = Number(activeChapter.dataset.altitude || 0);
        const to = Number(nextChapter.dataset.altitude || from);
        if (altitude) altitude.textContent = Math.round(from + (to - from) * segment).toLocaleString();
      };
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { update(); ticking = false; });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', update);
      activate(0);
      update();
    });
  };

  const initSummitNavigators = (scope = document) => {
    qa('[data-summit-navigator]', scope).forEach((navigator) => {
      if (navigator.dataset.summitBound === 'true') return;
      navigator.dataset.summitBound = 'true';
      const pins = qa('[data-summit-pin]', navigator);
      const cards = qa('[data-summit-card]', navigator);
      const controls = qa('[data-summit-control]', navigator);
      const activate = (index) => {
        pins.forEach((pin, i) => pin.classList.toggle('is-active', i === index));
        cards.forEach((card, i) => card.classList.toggle('is-active', i === index));
        controls.forEach((control, i) => control.classList.toggle('is-active', i === index));
      };
      pins.forEach((pin, index) => {
        pin.addEventListener('mouseenter', () => activate(index));
        pin.addEventListener('focus', () => activate(index));
        pin.addEventListener('click', () => activate(index));
      });
      controls.forEach((control, index) => control.addEventListener('click', () => activate(index)));
    });
  };

  const initReadinessKits = (scope = document) => {
    qa('[data-readiness-kit]', scope).forEach((kit) => {
      if (kit.dataset.kitBound === 'true') return;
      kit.dataset.kitBound = 'true';
      const items = qa('[data-kit-item]', kit);
      const count = q('[data-kit-count]', kit);
      const progress = q('[data-kit-progress]', kit);
      const stamp = q('[data-kit-stamp]', kit);
      const update = () => {
        const packed = items.filter((item) => item.classList.contains('is-packed')).length;
        if (count) count.textContent = packed;
        if (progress) progress.style.width = `${items.length ? packed / items.length * 100 : 0}%`;
        stamp?.classList.toggle('is-visible', packed === items.length && items.length > 0);
      };
      items.forEach((item) => item.addEventListener('click', () => {
        item.classList.toggle('is-packed');
        item.setAttribute('aria-pressed', String(item.classList.contains('is-packed')));
        update();
      }));
      update();
    });
  };

  const initArticle = (scope = document) => {
    qa('[data-article]', scope).forEach((article) => {
      if (article.dataset.articleBound === 'true') return;
      article.dataset.articleBound = 'true';
      const content = q('[data-article-content]', article);
      if (!content) return;
      const headings = qa('h2,h3', content);
      const toc = q('[data-toc]', article);
      const tocCard = q('[data-toc-card]', article);
      if (toc && headings.length) {
        headings.forEach((heading, index) => {
          if (!heading.id) heading.id = `trail-${index + 1}`;
          const link = document.createElement('a');
          link.href = `#${heading.id}`;
          link.textContent = heading.textContent.trim();
          if (heading.tagName === 'H3') link.classList.add('toc-link--nested');
          toc.appendChild(link);
        });
      } else if (tocCard) tocCard.hidden = true;
      const fill = q('[data-journey-fill]', article);
      const marker = q('[data-journey-marker]', article);
      const altitude = q('[data-article-altitude]', article);
      const stages = qa('[data-stage]', article);
      const tocLinks = toc ? qa('a', toc) : [];
      const update = () => {
        const rect = content.getBoundingClientRect();
        const scrollable = Math.max(content.offsetHeight - window.innerHeight * .42, 1);
        const progress = clamp((-rect.top + window.innerHeight * .27) / scrollable);
        if (fill) fill.style.width = `${progress * 100}%`;
        if (marker) marker.style.left = `${progress * 100}%`;
        if (altitude) altitude.textContent = `${Math.round(850 + progress * 2000).toLocaleString()} m`;
        const stageIndex = Math.min(stages.length - 1, Math.floor(progress * stages.length));
        stages.forEach((stage, index) => stage.classList.toggle('is-active', index === stageIndex));
        const currentStation = q('[data-current-station]', article);
        if (currentStation && stages[stageIndex]) currentStation.textContent = stages[stageIndex].textContent;
        let activeId = '';
        headings.forEach((heading) => { if (heading.getBoundingClientRect().top < 220) activeId = heading.id; });
        tocLinks.forEach((link) => link.classList.toggle('is-active', link.hash === `#${activeId}`));
      };
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  const initAccordions = (scope = document) => {
    qa('[data-accordion]', scope).forEach((group) => {
      if (group.dataset.accordionBound === 'true') return;
      group.dataset.accordionBound = 'true';
      qa('details', group).forEach((item) => item.addEventListener('toggle', () => {
        if (!item.open) return;
        qa('details', group).forEach((other) => { if (other !== item) other.open = false; });
      }));
    });
  };

  const initMagnetic = (scope = document) => {
    if (!document.body.classList.contains('has-magnetic') || reducedMotion() || !finePointer()) return;
    qa('[data-magnetic]:not([data-magnetic-bound])', scope).forEach((item) => {
      item.dataset.magneticBound = 'true';
      item.addEventListener('pointermove', (event) => {
        const rect = item.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        item.style.transform = `translate3d(${x * .14}px,${y * .16}px,0)`;
      });
      item.addEventListener('pointerleave', () => item.style.transform = '');
    });
  };

  const initTilt = (scope = document) => {
    if (!document.body.classList.contains('has-tilt') || reducedMotion() || !finePointer()) return;
    qa('[data-tilt-card]:not([data-tilt-bound])', scope).forEach((card) => {
      card.dataset.tiltBound = 'true';
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - .5;
        const y = (event.clientY - rect.top) / rect.height - .5;
        card.style.transform = `perspective(1100px) rotateX(${y * -4.5}deg) rotateY(${x * 5.5}deg) translateZ(0)`;
      });
      card.addEventListener('pointerleave', () => card.style.transform = '');
    });
  };

  const initPageUtilities = () => {
    const progress = q('[data-site-progress]');
    const backToTop = q('[data-back-to-top]');
    const update = () => {
      const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const ratio = clamp(window.scrollY / max);
      if (progress) progress.style.transform = `scaleX(${ratio})`;
      backToTop?.classList.toggle('is-visible', window.scrollY > 700);
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    backToTop?.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' }));
    update();
  };

  const initPageTransitions = () => {
    const overlay = q('[data-page-transition]');
    if (!overlay || reducedMotion() || window.Shopify?.designMode) return;
    qa('a[href]').forEach((link) => {
      if (link.dataset.transitionBound === 'true') return;
      link.dataset.transitionBound = 'true';
      link.addEventListener('click', (event) => {
        const url = new URL(link.href, window.location.href);
        const sameOrigin = url.origin === window.location.origin;
        const hashOnly = url.pathname === window.location.pathname && url.search === window.location.search && url.hash;
        if (!sameOrigin || hashOnly || link.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download')) return;
        event.preventDefault();
        overlay.classList.add('is-entering');
        window.setTimeout(() => window.location.href = url.href, 560);
      });
    });
    window.addEventListener('pageshow', () => overlay.classList.remove('is-entering'));
  };



  const initDominionHeroes = (scope = document) => {
    qa('[data-dominion-hero]', scope).forEach((hero) => {
      if (hero.dataset.dominionBound === 'true') return;
      hero.dataset.dominionBound = 'true';
      const heading = q('[data-split-heading]', hero);
      const spotlight = q('[data-rail-spotlight]', hero);
      const layers = qa('[data-rail-layer]', hero);
      const counter = q('[data-rail-counter]', hero);
      const flip = q('[data-flip-board]', hero);
      const destinations = qa('.departure-row strong', hero).map((item) => item.textContent.trim()).filter(Boolean);
      wrapHeadingWords(heading);
      requestAnimationFrame(() => hero.classList.add('is-ready'));
      if (counter) {
        const target = Number(counter.dataset.target || 92);
        window.setTimeout(() => animateNumber(counter, 0, target, 2200), 700);
      }
      if (flip && destinations.length > 1 && !reducedMotion()) {
        let index = 0;
        window.setInterval(() => {
          index = (index + 1) % destinations.length;
          flip.classList.add('is-flipping');
          window.setTimeout(() => {
            flip.textContent = destinations[index];
            flip.classList.remove('is-flipping');
          }, 220);
        }, 3400);
      }
      if (!reducedMotion() && finePointer()) {
        hero.addEventListener('pointermove', (event) => {
          const rect = hero.getBoundingClientRect();
          const x = clamp((event.clientX - rect.left) / rect.width);
          const y = clamp((event.clientY - rect.top) / rect.height);
          spotlight?.style.setProperty('--spot-x', `${x * 100}%`);
          spotlight?.style.setProperty('--spot-y', `${y * 100}%`);
        });
      }
      if (!reducedMotion() && document.body.classList.contains('has-parallax')) {
        let ticking = false;
        const update = () => {
          const rect = hero.getBoundingClientRect();
          const progress = clamp(-rect.top / Math.max(rect.height, 1));
          layers.forEach((layer) => {
            const strength = Number(layer.dataset.railLayer || .08);
            layer.style.transform = `translate3d(0,${progress * rect.height * strength}px,0)`;
          });
          ticking = false;
        };
        window.addEventListener('scroll', () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        }, { passive: true });
        update();
      }
    });
  };

  const initRouteConsoles = (scope = document) => {
    qa('[data-route-console]', scope).forEach((consoleEl) => {
      if (consoleEl.dataset.consoleBound === 'true') return;
      consoleEl.dataset.consoleBound = 'true';
      const tabs = qa('[data-console-tab]', consoleEl);
      const pins = qa('[data-console-pin]', consoleEl);
      const panels = qa('[data-console-panel]', consoleEl);
      const train = q('[data-console-train]', consoleEl);
      const route = q('[data-console-route]', consoleEl);
      if (route) {
        const length = route.getTotalLength();
        route.style.strokeDasharray = `${length}`;
        route.style.strokeDashoffset = reducedMotion() ? '0' : `${length}`;
        requestAnimationFrame(() => route.style.strokeDashoffset = '0');
      }
      const activate = (index, focus = false) => {
        tabs.forEach((tab, i) => {
          const active = i === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
          if (active && focus) tab.focus();
        });
        pins.forEach((pin, i) => pin.classList.toggle('is-active', i === index));
        panels.forEach((panel, i) => {
          panel.hidden = i !== index;
          panel.classList.toggle('is-active', i === index);
        });
        const pin = pins[index];
        if (train && pin) {
          train.style.left = pin.style.getPropertyValue('--pin-x');
          train.style.top = pin.style.getPropertyValue('--pin-y');
        }
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(index));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          activate(next, true);
        });
      });
      pins.forEach((pin, index) => pin.addEventListener('click', () => activate(index)));
      activate(0);
    });
  };

  const initRailTimelines = (scope = document) => {
    qa('[data-rail-timeline]', scope).forEach((section) => {
      if (section.dataset.timelineBound === 'true') return;
      section.dataset.timelineBound = 'true';
      const viewport = q('[data-timeline-viewport]', section);
      const track = q('[data-timeline-track]', section);
      const fill = q('[data-timeline-fill]', section);
      const train = q('[data-timeline-train]', section);
      const meter = q('[data-timeline-meter]', section);
      const number = q('[data-timeline-number]', section);
      const stops = qa('[data-timeline-stop]', section);
      if (!viewport || !track) return;
      const update = () => {
        const max = Math.max(viewport.scrollWidth - viewport.clientWidth, 1);
        const progress = clamp(viewport.scrollLeft / max);
        if (fill) fill.style.width = `${progress * 100}%`;
        if (meter) meter.style.width = `${progress * 100}%`;
        if (train) train.style.left = `${progress * (track.scrollWidth - 60) + 30}px`;
        let active = 0;
        const center = viewport.scrollLeft + viewport.clientWidth * .5;
        stops.forEach((stop, i) => {
          const stopCenter = stop.offsetLeft + stop.offsetWidth * .5;
          if (stopCenter <= center) active = i;
          stop.classList.toggle('is-current', i === active);
        });
        if (number) number.textContent = `${String(active + 1).padStart(2,'0')} / ${String(stops.length).padStart(2,'0')}`;
      };
      viewport.addEventListener('scroll', update, { passive: true });
      viewport.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || viewport.scrollWidth <= viewport.clientWidth) return;
        event.preventDefault();
        viewport.scrollLeft += event.deltaY;
      }, { passive: false });
      update();
    });
  };

  const initTicketChecklists = (scope = document) => {
    qa('[data-ticket-checklist]', scope).forEach((section) => {
      if (section.dataset.ticketBound === 'true') return;
      section.dataset.ticketBound = 'true';
      const checks = qa('[data-ticket-check]', section);
      const count = q('[data-readiness-count]', section);
      const fill = q('[data-readiness-fill]', section);
      const punch = q('[data-ticket-punch]', section);
      const update = () => {
        const checked = checks.filter((input) => input.checked).length;
        const total = Math.max(checks.length, 1);
        if (count) count.textContent = `${checked} / ${checks.length}`;
        if (fill) fill.style.width = `${checked / total * 100}%`;
        punch?.classList.toggle('is-visible', checked === checks.length && checks.length > 0);
      };
      checks.forEach((input) => input.addEventListener('change', update));
      update();
    });
  };

  const init = (scope = document) => {
    initMenus(scope);
    initReveal(scope);
    initAlpineHeroes(scope);
    initDominionHeroes(scope);
    initRouteConsoles(scope);
    initRailTimelines(scope);
    initTicketChecklists(scope);
    initRouteSelectors(scope);
    initAlpineAscents(scope);
    initSummitNavigators(scope);
    initReadinessKits(scope);
    initArticle(scope);
    initAccordions(scope);
    initMagnetic(scope);
    initTilt(scope);
  };

  document.addEventListener('DOMContentLoaded', () => {
    init();
    initHeaderScroll();
    initPageUtilities();
    initPageTransitions();
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
